// Hawk client (web) — `@hawk.so/javascript` v3. Подключается в main.tsx ДО
// ReactDOM.render, чтобы перехватить early errors. window.onerror +
// unhandledrejection — автоматически SDK. React-ошибки — через
// GlobalErrorBoundary (error-boundary.tsx).
//
// Privacy (ТЗ §3.7): beforeSend и beforeBreadcrumb зачищают PII клиента —
// никакие материалы / эссе / tutor-диалоги не попадают в payload,
// токены/auth — `<redacted>`. Правила scrub'а и состав сбора общие для всех
// браузерных поверхностей: `@studyhub/shared/observability`.
//
// Token не задан (dev без Hawk) → no-op (initHawkClient ничего не делает).
// Тип — импортом ТОЛЬКО типа: он стирается сборкой и не тянет 69 КБ SDK в
// стартовый граф. Значение приезжает динамическим импортом в initHawkClient.
import type HawkCatcher from "@hawk.so/javascript";
import {
	activeBrowserCatcher,
	browserCatcherCollectionOptions,
	createBrowserBeforeSend,
	createHawkThrottle,
	hashUserIdForHawk,
	hawkFingerprint,
	hawkRelease,
	registerBrowserCatcher,
	scrubPII,
} from "@studyhub/shared/observability";

// Предохранитель от шторма: залипший ретрай или ошибка в часто вызываемом
// эффекте иначе выжигают квоту проекта за минуты.
const allowSend = createHawkThrottle();

// Гейт автоматических событий SDK (window.onerror / unhandledrejection):
// заведомый браузерный шум не отправляем вовсе, повторы одного отпечатка
// гасим окном троттла. Ручные вызовы ниже проходят через свой `allowSend`.
const browserBeforeSend = createBrowserBeforeSend();

let _hawk: HawkCatcher | null = null;

/** Инициализация Hawk (вызывать в main.tsx ДО render). No-op без токена. */
export function initHawkClient(): void {
	// В Capacitor-сборке катчером владеет mobile-shell (свой проект Hawk и
	// свой токен, apps/mobile/src/observability/hawk-mobile.ts). Без этой
	// проверки fallback на VITE_HAWK_INTEGRATION_TOKEN поднял бы в WebView
	// второй катчер — каждая ошибка ушла бы дважды, в два разных проекта.
	if (__IS_CAPACITOR__) return;
	// `||`, а НЕ `??`: Dockerfile выставляет `ENV VITE_HAWK_TOKEN_WEB=` пустой
	// строкой, когда build-arg не передан, а пустая строка для `??` — вполне
	// себе значение. Из-за этого общий токен не подхватывался, токен становился
	// "", и минификатор вырезал катчер целиком как недостижимый код — молча.
	const token = import.meta.env.VITE_HAWK_TOKEN_WEB || import.meta.env.VITE_HAWK_INTEGRATION_TOKEN;
	if (!token || _hawk) return;

	// Буфер ранних ошибок ставится СИНХРОННО, до загрузки SDK. Сам катчер
	// приезжает динамическим импортом, и без буфера окно между первым кадром и
	// его загрузкой было бы слепым — а это ровно то окно, где живут ошибки
	// бутстрапа, ради которых инициализация и стоит до рендера.
	const early = captureEarlyErrors();

	void import("@hawk.so/javascript")
		.then(({ default: HawkCatcher }) => {
			try {
				_hawk = new HawkCatcher({
					token,
					release: hawkRelease(
						"web",
						import.meta.env.VITE_GIT_SHA || import.meta.env.VITE_APP_RELEASE,
					),
					collectorEndpoint: import.meta.env.VITE_HAWK_COLLECTOR_ENDPOINT || undefined,
					context: { app: "web", env: import.meta.env.MODE },
					beforeSend: browserBeforeSend,
					...browserCatcherCollectionOptions(),
				});
				registerBrowserCatcher(_hawk);
				early.flush();
			} catch {
				_hawk = null;
				early.stop();
			}
		})
		.catch(() => {
			// Катчер не загрузился — приложение это переживает молча, но слушатели
			// снимаем, иначе буфер растёт до конца сессии.
			early.stop();
		});
}

/**
 * Собрать ошибки, случившиеся до загрузки катчера, и отправить их, когда он
 * появится. Слушатели снимаются в любом исходе — и при успехе, и при отказе.
 */
function captureEarlyErrors(): { flush: () => void; stop: () => void } {
	const buffered: Array<{ error: unknown; ctx?: Record<string, unknown> }> = [];
	// Потолок — чтобы шторм ошибок на старте не съел память до загрузки SDK.
	const LIMIT = 20;
	const onError = (event: ErrorEvent): void => {
		if (buffered.length < LIMIT) buffered.push({ error: event.error ?? event.message });
	};
	const onRejection = (event: PromiseRejectionEvent): void => {
		if (buffered.length < LIMIT) buffered.push({ error: event.reason });
	};
	window.addEventListener("error", onError);
	window.addEventListener("unhandledrejection", onRejection);

	const stop = (): void => {
		window.removeEventListener("error", onError);
		window.removeEventListener("unhandledrejection", onRejection);
		buffered.length = 0;
	};
	return {
		stop,
		flush: () => {
			// Снимаем слушатели ДО отправки: дальше своё дело делает сам SDK, и
			// иначе каждая ошибка ушла бы дважды.
			window.removeEventListener("error", onError);
			window.removeEventListener("unhandledrejection", onRejection);
			for (const item of buffered) captureClientError(item.error, item.ctx);
			buffered.length = 0;
		},
	};
}

/**
 * Катчер этой сборки. В web им владеет модуль, в APK — mobile-shell, поэтому
 * берём активный из общего реестра, а не только собственный инстанс.
 */
function catcher(): ReturnType<typeof activeBrowserCatcher> {
	return _hawk ?? activeBrowserCatcher();
}

// Структурный аналог Hawk EventContext (Json). `@hawk.so/types` —
// транзитивная зависимость, импорт напрямую недоступен.
type HawkJsonNode = string | number | boolean | HawkJson;
interface HawkJson {
	[key: string]: HawkJsonNode;
}

/**
 * Ошибки без собственного стека, «адрес» которых SDK берёт из текста.
 *
 * Hawk перед отправкой парсит `error.stack` и докладывает к трейсу исходный
 * файл — фетчит его по имени из фрейма. У ошибок, которые бросает сам браузер
 * (`TypeError: Failed to fetch dynamically imported module: https://…`), стека
 * нет вовсе, парсер разбирает единственную строку — текст сообщения — и получает
 * «адрес» `typeerror: Failed to fetch…`. Дальше SDK честно идёт это скачивать,
 * а CSP блокирует запрос: пользователь видит в консоли две красные записи про
 * нарушение политики вместо одной понятной ошибки, и в Hawk уезжает мусорный
 * backtrace. Отдаём такую ошибку с нашим стеком — сообщение сохраняется, а
 * фреймы указывают на этот файл.
 */
function withParsableStack(error: Error): Error {
	if (/\n\s*at\s/.test(error.stack ?? "")) return error;
	const replacement = new Error(error.message);
	replacement.name = error.name;
	return replacement;
}

/** Ручная отправка ошибки (из ErrorBoundary / catch-блоков). Non-throwing. */
export function captureClientError(err: unknown, ctx?: Record<string, unknown>): void {
	const hawk = catcher();
	if (!hawk) return;
	try {
		const error = withParsableStack(err instanceof Error ? err : new Error(String(err)));
		if (!allowSend(hawkFingerprint(error))) return;
		// scrubPII + JSON round-trip → Json-совместимый context (без
		// undefined/функций/циклов).
		const context = ctx
			? (JSON.parse(JSON.stringify(scrubPII(ctx))) satisfies unknown as HawkJson)
			: undefined;
		hawk.send(error, context);
	} catch {
		// Трекинг не должен ронять UI.
	}
}

/** Хлебная крошка вручную — след для будущей ошибки (офлайн-очередь, SW). */
export function addClientBreadcrumb(
	message: string,
	data?: Record<string, unknown>,
	level: "info" | "warning" | "error" = "info",
): void {
	const hawk = catcher();
	if (!hawk) return;
	try {
		hawk.breadcrumbs.add({
			type: "logic",
			category: "app",
			message,
			level,
			data: data
				? (JSON.parse(JSON.stringify(scrubPII(data))) satisfies unknown as HawkJson)
				: undefined,
		});
	} catch {
		// игнор
	}
}

/**
 * Привязать affected-user (после login). В Hawk уходит только необратимый хеш
 * userId — тот же, что считает сервер, поэтому один инцидент сшивается между
 * фронтом и бэком, а персональных данных наружу не уезжает.
 */
export function setHawkUser(userId: string | null): void {
	const hawk = catcher();
	if (!hawk) return;
	try {
		if (!userId) {
			hawk.clearUser();
			return;
		}
		void hashUserIdForHawk(userId).then((hashed) => {
			if (hashed) catcher()?.setUser({ id: hashed });
		});
	} catch {
		// игнор
	}
}
